import { env } from '~/env'

type ActivityType =
  | 'PROFILE_VIEW'
  | 'EVENT_VIEW'
  | 'PROFILE_SHARE'
  | 'EVENT_SHARE'
  | 'EVENT_SAVED'
  | 'FACILITATOR_FOLLOWED'
  | 'REVIEW_SUBMITTED'

type TargetType = 'FACILITATOR' | 'EVENT' | 'MEMBER'

// Client-side deduplication: Map of "activityType_targetId" -> last sent timestamp
const recentActivity = new Map<string, number>()
const DEDUP_WINDOW_MS = 30_000 // 30 seconds

function shouldTrack(activityType: ActivityType, targetId: string): boolean {
  const key = `${activityType}_${targetId}`
  const lastSent = recentActivity.get(key)
  const now = Date.now()

  if (lastSent && now - lastSent < DEDUP_WINDOW_MS) {
    return false
  }

  recentActivity.set(key, now)
  return true
}

/**
 * Non-blocking activity signal tracker.
 * Deduplicates view events within a 30s window on the client side.
 * Fires request with fire-and-forget semantics. Never throws.
 */
export function trackActivity(
  activityType: ActivityType,
  targetType: TargetType,
  targetId: string,
  options?: {
    accessToken?: string
    metadata?: Record<string, unknown>
    skipDedup?: boolean
  },
): void {
  if (!options?.skipDedup && !shouldTrack(activityType, targetId)) {
    return
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (options?.accessToken) {
    headers['Authorization'] = `Bearer ${options.accessToken}`
  }

  fetch(`${env.NEXT_PUBLIC_API_URL}/activity-logs`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      activity_type: activityType,
      target_type: targetType,
      target_id: targetId,
      ...(options?.metadata ? { metadata: options.metadata } : {}),
    }),
    keepalive: true,
  }).catch(() => {
    // Silently ignore — tracking is non-critical
  })
}

/**
 * Sends a dwell time signal via navigator.sendBeacon.
 * Designed to be called on page unload / visibility change.
 * sendBeacon is more reliable than fetch during page teardown.
 */
export function trackDwellTime(
  activityType: 'EVENT_VIEW' | 'PROFILE_VIEW',
  targetType: TargetType,
  targetId: string,
  dwellTimeMs: number,
  accessToken?: string,
): void {
  // Ignore trivial dwell times (< 1s = likely a bounce/redirect)
  if (dwellTimeMs < 1000) return

  const payload = JSON.stringify({
    activity_type: activityType,
    target_type: targetType,
    target_id: targetId,
    metadata: { dwell_time_ms: dwellTimeMs },
  })

  // sendBeacon can't set Authorization headers, so for authenticated users
  // fall back to keepalive fetch. For anonymous users, use sendBeacon.
  if (accessToken) {
    fetch(`${env.NEXT_PUBLIC_API_URL}/activity-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: payload,
      keepalive: true,
    }).catch(() => {})
  } else {
    const blob = new Blob([payload], { type: 'application/json' })
    navigator.sendBeacon(`${env.NEXT_PUBLIC_API_URL}/activity-logs`, blob)
  }
}

// ---------------------------------------------------------------------------
// Personalisation engagement tracking (matching game + discovery search).
// All helpers are fire-and-forget and never throw — tracking is non-critical.
// ---------------------------------------------------------------------------

export type EngagementSection =
  | 'SWIPE_DECK'
  | 'PERSONALISED'
  | 'COMMUNITY'
  | 'INTEREST'
  | 'OUTSIDE_COMFORT_ZONE'
  | 'DISCOVERY'

export type InteractionAction =
  | 'VIEWED'
  | 'LIKED'
  | 'DISLIKED'
  | 'SKIPPED'
  | 'CLICKED_THROUGH'

export interface CardInteraction {
  event_id: string
  session_id?: string
  section: EngagementSection
  action: InteractionAction
  dwell_ms?: number
  position_in_deck?: number
}

// Views shorter than this are accidental taps / scroll-bys; the API drops
// them too, this just avoids the network call.
const MIN_VIEW_DWELL_MS = 1000

const pendingInteractions: CardInteraction[] = []
let flushTimer: ReturnType<typeof setTimeout> | undefined
let flushToken: string | undefined

function sendInteractions(interactions: CardInteraction[], accessToken?: string): void {
  if (interactions.length === 0) return
  fetch(`${env.NEXT_PUBLIC_API_URL}/engagement/interactions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ interactions }),
    keepalive: true,
  }).catch(() => {})
}

/**
 * Queue a card interaction; queued items are flushed in a batch after a short
 * debounce (and immediately on `flushInteractions`, e.g. visibilitychange).
 * VIEWED interactions under 1s are dropped. Requires an authenticated user.
 */
export function trackCardInteraction(
  interaction: CardInteraction,
  accessToken: string,
): void {
  if (
    interaction.action === 'VIEWED' &&
    (interaction.dwell_ms ?? 0) < MIN_VIEW_DWELL_MS
  ) {
    return
  }
  pendingInteractions.push(interaction)
  flushToken = accessToken
  if (flushTimer) clearTimeout(flushTimer)
  flushTimer = setTimeout(() => flushInteractions(), 2000)
}

/** Flush queued interactions immediately (call on unmount/visibilitychange). */
export function flushInteractions(): void {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = undefined
  }
  if (pendingInteractions.length === 0) return
  const batch = pendingInteractions.splice(0, pendingInteractions.length)
  sendInteractions(batch, flushToken)
}

/**
 * Log a discovery search (query + filters + result count). Debounce at the
 * call site — log on settled input, not per keystroke. Returns the search-log
 * id so a later click-through can be attributed via `logSearchClick`.
 */
export async function logSearch(params: {
  query: string
  filters?: Record<string, unknown>
  resultsCount?: number
  pageSource?: 'discovery_page' | 'facilitator_page'
  accessToken?: string
}): Promise<string | null> {
  try {
    const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/engagement/search-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(params.accessToken
          ? { Authorization: `Bearer ${params.accessToken}` }
          : {}),
      },
      body: JSON.stringify({
        query: params.query,
        filters: params.filters,
        results_count: params.resultsCount ?? 0,
        page_source: params.pageSource ?? 'discovery_page',
      }),
      keepalive: true,
    })
    if (!res.ok) return null
    const data = (await res.json()) as { id: string | null }
    return data.id
  } catch {
    return null
  }
}

/** Attribute a result click to a previously logged search. */
export function logSearchClick(
  searchLogId: string,
  eventId: string,
  accessToken?: string,
): void {
  fetch(
    `${env.NEXT_PUBLIC_API_URL}/engagement/search-logs/${searchLogId}/click`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ event_id: eventId }),
      keepalive: true,
    },
  ).catch(() => {})
}
